// Procedural WebAudio: engine loop, water spray noise, event chimes,
// boost whoosh, gate pass, lap fanfare, collision thud. No audio files.
export class AudioSystem {
  private context: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private engineOsc: OscillatorNode | null = null;
  private engineOsc2: OscillatorNode | null = null;
  private engineGain: GainNode | null = null;
  private sprayNoise: AudioBufferSourceNode | null = null;
  private sprayFilter: BiquadFilterNode | null = null;
  private sprayGain: GainNode | null = null;
  private unlocked = false;
  private duck = 1;
  muted = false;

  constructor() {
    const unlock = () => {
      void this.unlock();
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
  }

  async unlock(): Promise<void> {
    if (this.unlocked) return;
    const AudioContextClass =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextClass) return;
    this.context = new AudioContextClass();
    await this.context.resume();
    this.masterGain = this.context.createGain();
    this.masterGain.gain.value = 0.8;
    this.masterGain.connect(this.context.destination);
    this.startEngine();
    this.startSpray();
    this.unlocked = true;
  }

  private startEngine(): void {
    if (!this.context || !this.masterGain) return;
    const ctx = this.context;
    this.engineGain = ctx.createGain();
    this.engineGain.gain.value = 0.0001;

    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 900;

    this.engineOsc = ctx.createOscillator();
    this.engineOsc.type = 'sawtooth';
    this.engineOsc.frequency.value = 55;

    this.engineOsc2 = ctx.createOscillator();
    this.engineOsc2.type = 'square';
    this.engineOsc2.frequency.value = 27.5;

    this.engineOsc.connect(filter);
    this.engineOsc2.connect(filter);
    filter.connect(this.engineGain);
    this.engineGain.connect(this.masterGain);
    this.engineOsc.start();
    this.engineOsc2.start();
  }

  private startSpray(): void {
    if (!this.context || !this.masterGain) return;
    const ctx = this.context;
    const bufferSize = 2 * ctx.sampleRate;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;
    this.sprayNoise = ctx.createBufferSource();
    this.sprayNoise.buffer = buffer;
    this.sprayNoise.loop = true;
    this.sprayFilter = ctx.createBiquadFilter();
    this.sprayFilter.type = 'bandpass';
    this.sprayFilter.frequency.value = 1400;
    this.sprayFilter.Q.value = 0.7;
    this.sprayGain = ctx.createGain();
    this.sprayGain.gain.value = 0.0001;
    this.sprayNoise.connect(this.sprayFilter);
    this.sprayFilter.connect(this.sprayGain);
    this.sprayGain.connect(this.masterGain);
    this.sprayNoise.start();
  }

  // speed01: 0..1 normalized speed; boost: boost state.
  updateEngine(speed01: number, boost: boolean): void {
    if (!this.context || !this.engineGain || !this.engineOsc || !this.engineOsc2 || !this.sprayGain) return;
    const now = this.context.currentTime;
    const targetFreq = 48 + speed01 * 150 + (boost ? 60 : 0);
    this.engineOsc.frequency.setTargetAtTime(targetFreq, now, 0.08);
    this.engineOsc2.frequency.setTargetAtTime(targetFreq / 2, now, 0.08);
    this.engineGain.gain.setTargetAtTime((0.02 + speed01 * 0.075 + (boost ? 0.05 : 0)) * this.duck, now, 0.1);
    this.sprayGain.gain.setTargetAtTime(Math.pow(speed01, 2.2) * 0.11 * this.duck, now, 0.12);
    if (this.sprayFilter) {
      this.sprayFilter.frequency.setTargetAtTime(900 + speed01 * 2100, now, 0.15);
    }
  }

  setDuck(value: number): void {
    this.duck = value;
  }

  private chime(baseFreq: number, endFreq: number, duration: number, gainPeak: number, type: OscillatorType = 'triangle'): void {
    if (!this.context || !this.masterGain || this.context.state !== 'running') return;
    const ctx = this.context;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const now = ctx.currentTime;
    osc.type = type;
    osc.frequency.setValueAtTime(baseFreq, now);
    osc.frequency.exponentialRampToValueAtTime(endFreq, now + duration * 0.6);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(gainPeak * this.duck, now + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    osc.connect(gain);
    gain.connect(this.masterGain);
    osc.start(now);
    osc.stop(now + duration + 0.05);
  }

  pickup(index: number): void {
    this.chime(340 + index * 26, 720 + index * 30, 0.2, 0.09);
  }

  gate(): void {
    this.chime(520, 1040, 0.16, 0.11, 'sine');
    this.chime(780, 1560, 0.2, 0.06, 'sine');
  }

  lap(): void {
    this.chime(440, 880, 0.3, 0.12, 'sine');
    this.chime(660, 1320, 0.35, 0.08, 'sine');
    setTimeout(() => this.chime(880, 1760, 0.4, 0.09, 'sine'), 110);
  }

  boostPickup(): void {
    this.chime(220, 1200, 0.28, 0.12, 'sawtooth');
  }

  collision(strength: number): void {
    if (!this.context || !this.masterGain || this.context.state !== 'running') return;
    const ctx = this.context;
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'square';
    osc.frequency.setValueAtTime(90, now);
    osc.frequency.exponentialRampToValueAtTime(38, now + 0.22);
    gain.gain.setValueAtTime(0.18 * strength * this.duck, now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.28);
    osc.connect(gain);
    gain.connect(this.masterGain);
    osc.start(now);
    osc.stop(now + 0.3);
  }

  finish(): void {
    this.chime(523, 1046, 0.5, 0.12, 'sine');
    setTimeout(() => this.chime(659, 1318, 0.5, 0.1, 'sine'), 140);
    setTimeout(() => this.chime(784, 1568, 0.7, 0.11, 'sine'), 300);
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (this.masterGain && this.context) {
      this.masterGain.gain.setTargetAtTime(muted ? 0 : 0.8, this.context.currentTime, 0.05);
    }
  }

  dispose(): void {
    try {
      this.engineOsc?.stop();
      this.engineOsc2?.stop();
      this.sprayNoise?.stop();
    } catch {
      // already stopped
    }
    void this.context?.close();
    this.context = null;
  }
}
